// frontend/src/components/inventory/FilterProductSelector.js
import React, { useState, useEffect, useRef } from 'react';
import { FaSearch, FaChevronDown, FaTimes } from 'react-icons/fa';
import api from '../../utils/api';
import Loader from '../ui/Loader';

const FilterProductSelector = ({ 
  value, 
  onChange, 
  placeholder = "选择或搜索产品",
  maxDisplayItems = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);
  
  // 从value中找到选中的产品
  useEffect(() => {
    if (value && products.length > 0) {
      const product = products.find(p => p.id.toString() === value.toString());
      setSelectedProduct(product);
    } else if (!value) {
      setSelectedProduct(null);
    }
  }, [value, products]);
  
  // 点击外部关闭下拉框
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // 搜索产品
  const searchProducts = async (search = '') => {
    setLoading(true);
    try {
      const response = await api.get(`/products?search=${search}&limit=100`);
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Error searching products:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };
  
  // 初始加载产品
  useEffect(() => {
    searchProducts();
  }, []);
  
  // 搜索词变化时搜索
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      searchProducts(searchTerm);
      setHighlightedIndex(-1);
    }, 300);
    
    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);
  
  // 键盘导航
  const handleKeyDown = (e) => {
    const filteredProducts = products.filter(product =>
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.chinese_name && product.chinese_name.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredProducts.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : filteredProducts.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredProducts[highlightedIndex]) {
          handleProductSelect(filteredProducts[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
    }
  };
  
  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    onChange(product.id);
    setIsOpen(false);
    setSearchTerm('');
  };
  
  // 清除选择
  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedProduct(null);
    onChange('');
  };
  
  const handleOpen = () => {
    setIsOpen(true);
    setTimeout(() => {
      if (searchRef.current) {
        searchRef.current.focus();
      }
    }, 100);
  };
  
  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (product.chinese_name && product.chinese_name.toLowerCase().includes(searchTerm.toLowerCase()))
  ).slice(0, maxDisplayItems || products.length);
  
  return (
    <div className="relative" ref={dropdownRef}>
      {/* 选择框 */}
      <div
        onClick={handleOpen}
        className={`w-full p-2 border rounded-lg cursor-pointer bg-white flex items-center justify-between ${
          isOpen ? 'border-blue-500' : 'border-gray-300'
        }`}
      >
        <div className="flex-1">
          {selectedProduct ? (
            <div>
              <div className="font-medium">{selectedProduct.name}</div>
              {selectedProduct.chinese_name && (
                <div className="text-sm text-gray-500">{selectedProduct.chinese_name}</div>
              )}
            </div>
          ) : (
            <span className="text-gray-500">{placeholder}</span>
          )}
        </div>
        <div className="flex items-center">
          {selectedProduct && (
            <button
              onClick={handleClear}
              className="text-gray-400 hover:text-gray-600 mr-2"
              title="清除选择"
            >
              <FaTimes />
            </button>
          )}
          <FaChevronDown className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </div>
      
      {/* 下拉框 */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
          {/* 搜索框 */}
          <div className="p-3 border-b">
            <div className="relative">
              <FaSearch className="absolute left-3 top-3 text-gray-400" />
              <input
                ref={searchRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="搜索产品名称..."
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:border-blue-500"
              />
            </div>
            <div className="text-xs text-gray-500 mt-2">
              提示：使用 ↑↓ 键选择，回车确认，ESC 关闭
            </div>
          </div>
          
          {/* 产品列表 */}
          <div className="max-h-60 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center">
                <Loader size="sm" />
                <span className="ml-2 text-sm text-gray-500">搜索中...</span>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                没有找到匹配的产品
              </div>
            ) : (
              filteredProducts.map((product, index) => (
                <div
                  key={product.id}
                  onClick={() => handleProductSelect(product)}
                  className={`p-3 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                    selectedProduct?.id === product.id ? 'bg-blue-50' : 
                    highlightedIndex === index ? 'bg-gray-100' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="font-medium text-gray-900">{product.chinese_name || product.name}</div>
                  {product.chinese_name && product.name !== product.chinese_name && (
                    <div className="text-sm text-gray-600">{product.name}</div>
                  )}
                  <div className="text-xs text-gray-400 mt-1">
                    ID: {product.id} | 库存: {product.stock_quantity} | 价格: ¥{parseFloat(product.price).toFixed(2)}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterProductSelector;